import React from 'react';
import { Box, Typography, Paper, Button } from '@mui/material';
import { MapsHomeWork, Launch } from '@mui/icons-material';
import { canAccessExplorers } from '../../../utils/explorerAccess';
import { formatSchoolName } from '../../../utils/schoolUtils';
import { detectSchoolDataType } from '../../../utils/schoolDataDetection';
import { calculateAreaDemographics } from '../../../utils/areaCalculations';

const DemographicAnalysis = ({ 
  selectedSchool,
  selectedSchoolTrends,
  nearbySchools,
  schoolsEnrollmentData,
  selectedGrades,
  selectedDriveTime,
  esriData,
  user
}) => {
  if (!selectedSchool) {
    return null;
  }

  // Detect what type of data is available for this school/location
  const { isDropAPinLocation } = detectSchoolDataType(selectedSchool, schoolsEnrollmentData);

  // Create URL with school parameter
  const explorerUrl = `/geographic-explorer?school=${selectedSchool.uuid}`;

  // Helper function to get largest demographic group from ESRI data
  const getESRIDemographics = (esriData, selectedDriveTime) => {
    const driveTimeData = esriData?.drive_times?.[selectedDriveTime];
    if (!driveTimeData) return null;

    // Map ESRI demographic fields to readable names
    const demographics = {
      'White': driveTimeData.per_wht_child_20 || 0,
      'Hispanic': driveTimeData.per_hisp_child_20 || 0,
      'Black': driveTimeData.per_blk_child_20 || 0,
      'Asian': driveTimeData.per_asn_child_20 || 0,
      'American Indian': driveTimeData.per_ai_child_20 || 0,
      'Pacific Islander': driveTimeData.per_pi_child_20 || 0,
      'Two or More Races': driveTimeData.per_two_or_more_child_20 || 0
    };

    // Find the largest group
    let largestGroup = null;
    let largestPercentage = 0;

    Object.entries(demographics).forEach(([group, percentage]) => {
      if (percentage > largestPercentage) {
        largestGroup = group;
        largestPercentage = percentage;
      }
    });

    return largestGroup && largestPercentage > 0 ? {
      group: largestGroup,
      percentage: Math.round(largestPercentage)
    } : null;
  };

  // Format the demographic difference text
  const getDemographicText = () => {
    if (isDropAPinLocation) {
      // Try to use ESRI demographic data first
      const esriDemographics = getESRIDemographics(esriData, selectedDriveTime);
      
      if (esriDemographics) {
        return {
          hasValidData: true,
          main: formatSchoolName(selectedSchool.name),
          group: esriDemographics.group,
          percentage: `${esriDemographics.percentage}%`,
          source: 'ESRI',
          isLocationOnly: true
        };
      }

      // Fallback to nearby schools data if ESRI data is not available
      if (!nearbySchools || Object.keys(nearbySchools.current || {}).length === 0) {
        return {
          hasValidData: false,
          message: `Demographic data is not available for the area around ${formatSchoolName(selectedSchool.name)}.`,
          main: formatSchoolName(selectedSchool.name),
          isLocationOnly: true
        };
      }
      
      // Calculate area demographics without the selected location
      const areaDemographics = calculateAreaDemographics({
        nearbySchools,
        schoolsEnrollmentData,
        selectedGrades: selectedGrades || ['Kindergarten', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'],
        selectedDriveTime: selectedDriveTime || 10
      });
      
      if (!areaDemographics.largestGroup || areaDemographics.totalEnrollment === 0) {
        return {
          hasValidData: false,
          message: `Limited demographic data available for the area around ${formatSchoolName(selectedSchool.name)}.`,
          main: formatSchoolName(selectedSchool.name),
          isLocationOnly: true
        };
      }

      const percentage = Math.round(areaDemographics.largestGroupPercentage);
      
      return {
        hasValidData: true,
        main: formatSchoolName(selectedSchool.name),
        group: areaDemographics.largestGroup,
        percentage: `${percentage}%`,
        schoolCount: areaDemographics.schoolsWithDemographicData,
        source: 'nearby schools',
        isLocationOnly: true
      };
    }

    // Existing logic for schools with enrollment data
    if (!selectedSchoolTrends) {
      return {
        hasValidData: false,
        message: 'Demographic comparison data is not available.',
        main: formatSchoolName(selectedSchool?.name) || 'This school',
        difference: 'N/A',
        group: 'N/A',
        direction: '',
        change: null,
        hasChange: false,
        isIncrease: false,
        isLocationOnly: false
      };
    }
    // Check if we have valid demographic data
    if (!selectedSchoolTrends?.largestGroup || 
        selectedSchoolTrends?.hasDemographicData !== true ||
        typeof selectedSchoolTrends?.demographicDifference !== 'number' ||
        isNaN(selectedSchoolTrends?.demographicDifference)) {
      return {
        hasValidData: false,
        message: 'Demographic comparison data is not available.',
        main: formatSchoolName(selectedSchool?.name) || 'This school',
        difference: 'N/A',
        group: 'N/A',
        direction: '',
        change: null,
        hasChange: false,
        isIncrease: false
      };
    }
  
    const difference = Math.abs(selectedSchoolTrends.demographicDifference);
    const direction = selectedSchoolTrends.demographicDifference >= 0 ? 'higher' : 'lower';
    const groupText = selectedSchoolTrends.largestGroup;
  
    return {
      hasValidData: true,
      main: formatSchoolName(selectedSchool.name),
      difference: `${Math.round(difference)}%`,
      group: groupText,
      change: selectedSchoolTrends.largestGroupChange ? `${Math.round(Math.abs(selectedSchoolTrends.largestGroupChange))}%` : null,
      direction,
      hasChange: !!selectedSchoolTrends.largestGroupChange,
      isIncrease: selectedSchoolTrends.largestGroupChange > 0
    };
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Paper 
        elevation={0}
        sx={{
          mb: 2,
          py: 2,
          px: 3,
          backgroundColor: 'grey.50',
          color: 'text.primary',
          borderRadius: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2,
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: 6,
            backgroundColor: 'primary.main',
            borderTopLeftRadius: 8,
            borderBottomLeftRadius: 8
          }
        }}
      >
        <Box sx={{ flex: 1 }}>
          <Typography variant="h8" sx={{ fontWeight: 200 }}>
            {getDemographicText().hasValidData ? (
              getDemographicText().isLocationOnly ? (
                <>
                  The area around <Box component="span" sx={{ fontWeight: 600 }}>{getDemographicText().main}</Box> serves primarily{' '}
                  <Box component="span" sx={{ fontWeight: 600 }}>{getDemographicText().group}</Box> children{' '}
                  (<Box component="span" sx={{ fontWeight: 600 }}>{getDemographicText().percentage}</Box>){' '}
                  {getDemographicText().source === 'ESRI' ? (
                    'based on area demographic data.'
                  ) : (
                    <>
                      based on {getDemographicText().schoolCount} nearby school{getDemographicText().schoolCount !== 1 ? 's' : ''} with demographic data.
                    </>
                  )}{' '}
                  {getDemographicText().source === 'ESRI' ? 
                    'This location would serve a similar demographic profile.' :
                    'This location would serve a similar demographic profile.'
                  }
                </>
              ) : (
                <>
                  <Box component="span" sx={{ fontWeight: 600 }}>{getDemographicText().main}</Box> serves a{' '}
                  <Box component="span" sx={{ fontWeight: 600 }}>{getDemographicText().difference}</Box> {getDemographicText().direction} percentage of{' '}
                  <Box component="span" sx={{ fontWeight: 600 }}>{getDemographicText().group}</Box> students compared to nearby schools
                  {getDemographicText().hasChange && (
                      <>, with a <Box component="span" sx={{ fontWeight: 600 }}>{getDemographicText().change}</Box> {
                      getDemographicText().isIncrease ? 'increase' : 'decrease'
                      } since 2019</>
                  )}.
                </>
              )
            ) : (
              getDemographicText().message
            )}
          </Typography>
        </Box>
        {canAccessExplorers(user?.role) && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<MapsHomeWork />}
            endIcon={<Launch />}
            href={explorerUrl}
            sx={{ 
              backgroundColor: 'primary.dark',
              color: 'white',
              '&:hover': {
                backgroundColor: 'primary.main'
              }
            }}
          >
            OPEN IN GEOGRAPHIC EXPLORER
          </Button>
        )}
      </Paper>
    </Box>
  );
};

export default DemographicAnalysis;